import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Trash2, Flag, Sparkles } from "lucide-react";

// GM "Checkpoint" tool — flags a story beat ahead of time (Main Quest = epic, Side Quest =
// regular), then Activates it when the table actually reaches that moment, awarding XP or a
// level directly to the party. Activating fires the Auto-Leveling Engine + a Sir Emberbeard
// message flavored by the checkpoint's type and optional custom prompt.
export default function CheckpointsPanel({ gameId, isGm }) {
  const [checkpoints, setCheckpoints] = useState([]);
  const [name, setName] = useState("");
  const [checkpointType, setCheckpointType] = useState("side_quest");
  const [awardType, setAwardType] = useState("xp");
  const [xpAmount, setXpAmount] = useState(500);
  const [targetLevel, setTargetLevel] = useState(2);
  const [customPrompt, setCustomPrompt] = useState("");

  const load = async () => {
    const { data } = await api.get(`/games/${gameId}/checkpoints`);
    setCheckpoints(data);
  };

  useEffect(() => { load(); }, [gameId]);

  const create = async () => {
    if (!name.trim()) return toast.error("Give the checkpoint a name");
    await api.post(`/games/${gameId}/checkpoints`, {
      name, checkpoint_type: checkpointType, award_type: awardType,
      xp_amount: awardType === "xp" ? xpAmount : null,
      target_level: awardType === "level" ? targetLevel : null,
      custom_prompt: customPrompt || null,
    });
    setName(""); setCustomPrompt("");
    toast.success("Checkpoint flagged");
    load();
  };

  const activate = async (id) => {
    await api.post(`/games/${gameId}/checkpoints/${id}/activate`);
    toast.success("Checkpoint activated - the party's story just moved forward!");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/games/${gameId}/checkpoints/${id}`);
    load();
  };

  return (
    <div className="flex flex-col h-full p-4 gap-4 overflow-y-auto scrollbar-thin" data-testid="checkpoints-panel">
      {isGm && (
        <div className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-3">
          <Input data-testid="checkpoint-name-input" placeholder="Checkpoint name (e.g. Defeat the Goblin Chief)" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10" />
          <div className="flex gap-2">
            <Select value={checkpointType} onValueChange={setCheckpointType}>
              <SelectTrigger data-testid="checkpoint-type-select" className="flex-1 bg-black/30 border-white/10 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="main_quest">Main Quest (epic)</SelectItem>
                <SelectItem value="side_quest">Side Quest (regular)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={awardType} onValueChange={setAwardType}>
              <SelectTrigger data-testid="checkpoint-award-type-select" className="flex-1 bg-black/30 border-white/10 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="xp">Award XP</SelectItem>
                <SelectItem value="level">Set Level</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {awardType === "xp" ? (
            <Input data-testid="checkpoint-xp-input" type="number" min={0} value={xpAmount} onChange={(e) => setXpAmount(parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 w-32" />
          ) : (
            <Input data-testid="checkpoint-level-input" type="number" min={1} max={20} value={targetLevel} onChange={(e) => setTargetLevel(parseInt(e.target.value, 10) || 1)} className="bg-black/30 border-white/10 w-32" />
          )}
          <Textarea data-testid="checkpoint-custom-prompt-input" placeholder="Custom note for Sir Emberbeard (optional) - makes his congrats personal" value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} className="bg-black/30 border-white/10" rows={2} />
          <Button data-testid="checkpoint-create-button" size="sm" onClick={create} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] gap-1.5">
            <Flag className="w-3.5 h-3.5" /> Flag Checkpoint
          </Button>
        </div>
      )}
      <div className="space-y-3">
        {checkpoints.map((c) => (
          <div key={c.id} data-testid={`checkpoint-${c.id}`} className="bg-black/20 border border-white/5 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display text-lg leading-tight flex items-center gap-1.5">
                  {c.checkpoint_type === "main_quest" && <Sparkles className="w-4 h-4 text-[#D4AF37]" />} {c.name}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/70">
                  {c.checkpoint_type === "main_quest" ? "Main Quest" : "Side Quest"} - {c.award_type === "xp" ? `${c.xp_amount} XP` : `Level ${c.target_level}`} - {c.status}
                </p>
              </div>
              {isGm && c.status === "pending" && (
                <div className="flex gap-1.5 shrink-0">
                  <Button data-testid={`checkpoint-activate-${c.id}`} size="sm" onClick={() => activate(c.id)} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e] h-7 text-xs">Activate</Button>
                  <button data-testid={`checkpoint-delete-${c.id}`} onClick={() => remove(c.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            {c.custom_prompt && <p className="text-sm text-[#f1e0c5]/70 mt-2 whitespace-pre-wrap italic">"{c.custom_prompt}"</p>}
          </div>
        ))}
        {checkpoints.length === 0 && <p className="text-sm text-[#f1e0c5]/30 text-center pt-6">No checkpoints yet - the story starts here.</p>}
      </div>
    </div>
  );
}
